import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { motion, useReducedMotion } from "framer-motion";
import WhopColored from "../images/whop_logo_symbol_color.png";
import Input from "../components/Input";
import GrailedImage from "../images/grailed.svg";
import DepopImage from "../images/logos/depop.svg";
import EbayImage from "../images/logos/ebay.svg";
import EtsyImage from "../images/logos/etsy.svg";
import FacebookImage from "../images/logos/facebook.svg";
import ShopifyImage from "../images/logos/shopify.svg";
import PoshmarkImage from "../images/logos/POSH.svg";
import PoizonImage from "../images/logos/poizon.png";
import TrackNListLogo from "../images/IconOnly_Transparent_NoBuffer.png";
import {
  getAcquisitionContext,
  getAnalyticsHeaders,
  storeAcquisitionContext,
  track,
} from "../analytics";

const SIGNUP_DISABLED = false;

const plans = ["Starter", "Basic", "Premium", "Enterprise"];
const priceIdMapping: { [key: string]: { [key: string]: string } } = {
  Starter: {
    month: "plan_ShVBDeoCN3Ef9", // Updated from price_1Q9i4CRwi8cbrWgq2SkImsFF
    year: "plan_ShVBDeoCN3Ef9", // Updated from price_1Q9i4DRwi8cbrWgqhsDqMUIk
  },
  Basic: {
    month: "plan_G942nmt3YIGP9", // Updated from price_1Q9i4FRwi8cbrWgq1HzkR61o
    year: "plan_jGS89ALOVTToW", // Updated from price_1Q9i4GRwi8cbrWgq4P2ruFB1
  },
  Premium: {
    month: "plan_wiu89Mm2yaKUl", // Updated from price_1Q9i4HRwi8cbrWgq1dY3KNvA
    year: "plan_HO26n62AaI13h", // Updated from price_1Q9i4IRwi8cbrWgq3dMlCoXx
  },
  Enterprise: {
    month: "plan_gfs0wZAtQIeiK", // Updated from price_1Q9i4KRwi8cbrWgqg9xdtGQZ
    year: "plan_hn1h683EDrmjM", // Updated from price_1Q9i4LRwi8cbrWgqgUXZO6yE
  },
};
const getPriceId = (plan: string, interval: string) => {
  return priceIdMapping[plan]?.[interval] || "plan_ShVBDeoCN3Ef9"; // Updated default from price_1Q9i4CRwi8cbrWgq2SkImsFF
};

// Professional animation component for signup page
const SignupAnimation: React.FC = () => {
  const shouldReduceMotion = useReducedMotion();

  // 11 platforms with their logos - random initial positions
  const platforms = [
    { name: "eBay", src: EbayImage },
    { name: "Grailed", src: GrailedImage },
    {
      name: "Mercari",
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7f/Mercari_logo_2018.svg/440px-Mercari_logo_2018.svg.png",
    },
    { name: "Shopify", src: ShopifyImage },
    {
      name: "StockX",
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/5/58/StockX_logo.svg/440px-StockX_logo.svg.png",
    },
    { name: "Poshmark", src: PoshmarkImage },
    { name: "Poizon", src: PoizonImage },
    {
      name: "Amazon",
      src: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/06/Amazon_2024.svg/400px-Amazon_2024.svg.png",
    },
    { name: "Etsy", src: EtsyImage },
    { name: "Depop", src: DepopImage },
    { name: "Facebook", src: FacebookImage },
  ];

  // Generate random positions around the center logo with collision detection
  const minDistanceFromCenter = 200; // Minimum distance from center (px) - increased
  const maxDistanceFromCenter = 450; // Maximum distance from center (px) - increased
  const platformBubbleSize = 80; // Platform bubble size (w-20 = 5rem = 80px)
  const platformRadius = platformBubbleSize / 2; // 40px
  const minDistanceBetweenPlatforms = platformBubbleSize + 30; // Minimum distance between platforms (110px) - increased
  const logoSize = 128; // Central logo size (w-32 = 8rem = 128px)
  const logoRadius = logoSize / 2; // 64px
  const minDistanceFromLogo = logoRadius + platformRadius + 50; // Minimum distance from logo center (154px) - increased
  const maxAttempts = 500; // Maximum attempts to find a valid position

  // Logo position (top-[35%] = 35%, left-[45%] = 45%)
  // Convert to pixel offsets from viewport center (assuming typical viewport)
  // For collision detection, we'll use a reference point
  // Since logo is at 45% left and 35% top, relative to viewport center (50%, 50%)
  const logoOffsetX = -5; // 45% - 50% = -5% (left of center)
  const logoOffsetY = -15; // 35% - 50% = -15% (above center)
  // Convert to approximate pixels (assuming 1920px width, 1% ≈ 19.2px)
  const logoOffsetXPx = logoOffsetX * 19.2;
  const logoOffsetYPx = logoOffsetY * 19.2;

  const generateRandomPositions = () => {
    const positions: Array<{
      x: string;
      y: string;
      angle: number;
      xPercent: number;
      yPercent: number;
      xPx: number;
      yPx: number;
    }> = [];

    for (let i = 0; i < platforms.length; i++) {
      let attempts = 0;
      let validPosition = false;
      let offsetX = 0;
      let offsetY = 0;
      let angle = 0;
      let distance = 0;

      while (!validPosition && attempts < maxAttempts) {
        // Generate random angle and distance
        angle = Math.random() * 2 * Math.PI;
        distance =
          minDistanceFromCenter +
          Math.random() * (maxDistanceFromCenter - minDistanceFromCenter);

        // Calculate position using trigonometry
        offsetX = distance * Math.cos(angle);
        offsetY = distance * Math.sin(angle);

        // Check distance from logo (accounting for logo's actual position)
        // Logo is at 45% left, 35% top, which is offset from viewport center
        const dxFromLogo = offsetX - logoOffsetXPx;
        const dyFromLogo = offsetY - logoOffsetYPx;
        const distanceFromLogo = Math.sqrt(
          dxFromLogo * dxFromLogo + dyFromLogo * dyFromLogo,
        );
        if (distanceFromLogo < minDistanceFromLogo) {
          attempts++;
          continue;
        }

        // Check collision with existing platforms
        validPosition = true;
        for (const existingPos of positions) {
          const dx = offsetX - existingPos.xPx;
          const dy = offsetY - existingPos.yPx;
          const distanceBetween = Math.sqrt(dx * dx + dy * dy);

          if (distanceBetween < minDistanceBetweenPlatforms) {
            validPosition = false;
            break;
          }
        }

        attempts++;
      }

      // Use calc() to center and add pixel offset
      const x = `calc(50% + ${offsetX.toFixed(2)}px)`;
      const y = `calc(50% + ${offsetY.toFixed(2)}px)`;

      // Store as percentages for collision detection (approximate conversion)
      const xPercent = 50 + offsetX / 10; // Rough approximation
      const yPercent = 50 + offsetY / 10; // Rough approximation

      positions.push({
        x,
        y,
        angle,
        xPercent,
        yPercent,
        xPx: offsetX,
        yPx: offsetY,
      });
    }

    return positions;
  };

  const positions = generateRandomPositions();

  // Generate positions with random placement
  const platformsWithPositions = platforms.map((platform, idx) => {
    const pos = positions[idx];

    // Random movement for each platform
    const moveRangeX = 15 + Math.random() * 20; // 15-35px
    const moveRangeY = 15 + Math.random() * 20; // 15-35px
    const moveOffsetX1 = (Math.random() - 0.5) * moveRangeX;
    const moveOffsetY1 = (Math.random() - 0.5) * moveRangeY;
    const moveOffsetX2 = (Math.random() - 0.5) * moveRangeX;
    const moveOffsetY2 = (Math.random() - 0.5) * moveRangeY;

    const duration = 8 + Math.random() * 6; // 8-14 seconds
    const delay = idx * 0.1 + Math.random() * 0.3;

    return {
      ...platform,
      initialX: pos.x,
      initialY: pos.y,
      angle: pos.angle,
      moveOffsetX1,
      moveOffsetY1,
      moveOffsetX2,
      moveOffsetY2,
      duration,
      delay,
    };
  });

  // Generate decorative bubbles with collision detection (smaller than platform bubbles)
  const generateDecorativeBubbles = () => {
    const bubbles: Array<{
      size: number;
      x: string;
      y: string;
      moveRangeX: number;
      moveRangeY: number;
      moveOffsetX1: number;
      moveOffsetX2: number;
      moveOffsetY1: number;
      moveOffsetY2: number;
      duration: number;
      delay: number;
      opacity: number;
    }> = [];
    const maxAttempts = 300;
    // More reasonable distance thresholds
    const minDistanceFromPlatform = 5; // Minimum distance from platform bubbles (in %)
    const minDistanceFromCenter = 15; // Minimum distance from center logo (in %)

    for (let i = 0; i < 45; i++) {
      let attempts = 0;
      let validPosition = false;
      let x = 0;
      let y = 0;
      const size = 8 + Math.random() * 16; // 8-24px
      // Convert size to percentage for distance calculations (rough approximation)
      const sizePercent = size / 10; // Approximate conversion

      while (!validPosition && attempts < maxAttempts) {
        // Generate random position
        x = 8 + Math.random() * 84; // 8% to 92% (more margin)
        y = 8 + Math.random() * 84; // 8% to 92%

        validPosition = true;

        // Check collision with platform bubbles
        for (const pos of positions) {
          const distance = Math.sqrt(
            Math.pow(x - pos.xPercent, 2) + Math.pow(y - pos.yPercent, 2),
          );
          // Platform bubbles are ~80px = ~8% in our approximation
          if (distance < minDistanceFromPlatform + sizePercent / 2 + 4) {
            validPosition = false;
            break;
          }
        }

        // Check collision with center (TrackNList logo)
        if (validPosition) {
          const centerDistance = Math.sqrt(
            Math.pow(x - 50, 2) + Math.pow(y - 50, 2),
          );
          // Center logo is ~128px = ~12.8% in our approximation
          if (centerDistance < minDistanceFromCenter + sizePercent / 2) {
            validPosition = false;
          }
        }

        // Check collision with existing decorative bubbles
        if (validPosition) {
          for (const bubble of bubbles) {
            const bubbleX = parseFloat(bubble.x.replace("%", ""));
            const bubbleY = parseFloat(bubble.y.replace("%", ""));
            const distance = Math.sqrt(
              Math.pow(x - bubbleX, 2) + Math.pow(y - bubbleY, 2),
            );
            // Minimum distance = sum of radii + padding
            const bubbleSizePercent = bubble.size / 10;
            const minDist = sizePercent / 2 + bubbleSizePercent / 2 + 1.2; // 1.2% padding
            if (distance < minDist) {
              validPosition = false;
              break;
            }
          }
        }

        attempts++;
      }

      // Add bubble even if we couldn't find perfect position (with warning)
      if (!validPosition && attempts >= maxAttempts) {
        // Fallback: place it anyway but with smaller movement range
        x = 10 + Math.random() * 80;
        y = 10 + Math.random() * 80;
      }

      const moveRangeX = 6 + Math.random() * 12; // 6-18px (further reduced)
      const moveRangeY = 6 + Math.random() * 12; // 6-18px
      const duration = 10 + Math.random() * 8; // 10-18 seconds
      const delay = i * 0.08 + Math.random() * 0.2;
      const opacity = 0.2 + Math.random() * 0.4; // 0.2-0.6 opacity
      // Pre-calculate random movement offsets
      const moveOffsetX1 = (Math.random() - 0.5) * moveRangeX;
      const moveOffsetX2 = (Math.random() - 0.5) * moveRangeX;
      const moveOffsetY1 = (Math.random() - 0.5) * moveRangeY;
      const moveOffsetY2 = (Math.random() - 0.5) * moveRangeY;

      bubbles.push({
        size,
        x: `${x}%`,
        y: `${y}%`,
        moveRangeX,
        moveRangeY,
        moveOffsetX1,
        moveOffsetX2,
        moveOffsetY1,
        moveOffsetY2,
        duration,
        delay,
        opacity,
      });
    }

    return bubbles;
  };

  const decorativeBubbles = generateDecorativeBubbles();

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      {/* Animated background gradient orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-br from-softBlue/20 to-transparent rounded-full blur-3xl"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  x: [0, 30, 0],
                  y: [0, -20, 0],
                  scale: [1, 1.1, 1],
                }
          }
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-brightPurple/20 to-transparent rounded-full blur-3xl"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  x: [0, -30, 0],
                  y: [0, 20, 0],
                  scale: [1, 1.1, 1],
                }
          }
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        <motion.div
          className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-deepPurple/15 to-transparent rounded-full blur-3xl"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  x: [0, 20, 0],
                  y: [0, 30, 0],
                  scale: [1, 1.2, 1],
                }
          }
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Decorative bubbles (smaller, different sizes) */}
      {decorativeBubbles.map((bubble, idx) => (
        <motion.div
          key={`decorative-${idx}`}
          className="absolute rounded-full"
          style={{
            left: bubble.x,
            top: bubble.y,
            width: `${bubble.size}px`,
            height: `${bubble.size}px`,
            transform: "translate(-50%, -50%)",
            zIndex: 1,
            background: `linear-gradient(135deg, rgba(108, 125, 255, ${
              bubble.opacity
            }), rgba(123, 94, 255, ${
              bubble.opacity * 0.7
            }), rgba(93, 63, 211, ${bubble.opacity * 0.5}))`,
            boxShadow: `0 4px 15px rgba(108, 125, 255, ${
              bubble.opacity * 0.3
            })`,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={
            shouldReduceMotion
              ? { scale: 1, opacity: bubble.opacity }
              : {
                  scale: [0, 1.2, 1],
                  opacity: [0, bubble.opacity, bubble.opacity],
                  x: [0, bubble.moveOffsetX1, bubble.moveOffsetX2, 0],
                  y: [0, bubble.moveOffsetY1, bubble.moveOffsetY2, 0],
                }
          }
          transition={{
            scale: {
              duration: 0.8,
              delay: bubble.delay,
              ease: "easeOut",
            },
            opacity: {
              duration: 0.8,
              delay: bubble.delay,
              ease: "easeOut",
            },
            x: {
              duration: bubble.duration,
              repeat: Infinity,
              repeatType: "reverse" as const,
              ease: "easeInOut",
              delay: bubble.delay + 0.8,
            },
            y: {
              duration: bubble.duration * 1.2,
              repeat: Infinity,
              repeatType: "reverse" as const,
              ease: "easeInOut",
              delay: bubble.delay + 0.8,
            },
          }}
        />
      ))}

      {/* Platform bubbles randomly positioned around the logo */}
      {platformsWithPositions.map((platform, idx) => (
        <motion.div
          key={idx}
          className="absolute"
          style={{
            left: platform.initialX,
            top: platform.initialY,
            transform: "translate(-50%, -50%)",
            zIndex: 3,
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={
            shouldReduceMotion
              ? { scale: 1, opacity: 1 }
              : {
                  scale: [0, 1.2, 1],
                  opacity: [0, 1, 1],
                  x: [0, platform.moveOffsetX1, platform.moveOffsetX2, 0],
                  y: [0, platform.moveOffsetY1, platform.moveOffsetY2, 0],
                }
          }
          transition={{
            scale: {
              duration: 0.6,
              delay: platform.delay,
              ease: "easeOut",
            },
            opacity: {
              duration: 0.6,
              delay: platform.delay,
              ease: "easeOut",
            },
            x: {
              duration: platform.duration,
              repeat: Infinity,
              repeatType: "reverse" as const,
              ease: "easeInOut",
              delay: platform.delay + 0.6,
            },
            y: {
              duration: platform.duration * 1.3,
              repeat: Infinity,
              repeatType: "reverse" as const,
              ease: "easeInOut",
              delay: platform.delay + 0.6,
            },
          }}
        >
          <motion.div
            className="relative"
            animate={
              shouldReduceMotion
                ? {}
                : {
                    rotate: [0, 5, -5, 0],
                  }
            }
            transition={{
              duration: 4 + idx * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: platform.delay + 0.6,
            }}
          >
            {/* Bubble with platform logo */}
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 shadow-lg shadow-softBlue/20 dark:shadow-brightPurple/20 border-2 border-softBlue/30 dark:border-brightPurple/30 flex items-center justify-center p-3 hover:border-softBlue/50 dark:hover:border-brightPurple/50 hover:shadow-xl hover:shadow-softBlue/30 dark:hover:shadow-brightPurple/30 transition-all duration-300">
              <img
                src={platform.src}
                alt={platform.name}
                className="w-full h-full object-contain"
              />
            </div>
            {/* Bubble shine effect */}
            <div className="absolute top-2 left-2 w-4 h-4 rounded-full bg-white/40 dark:bg-white/20 blur-sm" />
          </motion.div>
        </motion.div>
      ))}

      {/* Central TrackNList Logo Bubble - centered horizontally, moved up */}
      <motion.div
        className="absolute top-[35%] left-[45%] transform -translate-x-1/2 -translate-y-1/2 z-20"
        initial={{ scale: 0, opacity: 0 }}
        animate={
          shouldReduceMotion
            ? { scale: 1, opacity: 1 }
            : {
                scale: [0, 1.2, 1],
                opacity: [0, 1, 1],
              }
        }
        transition={{
          duration: 0.8,
          delay: 1.5,
          ease: "easeOut",
        }}
      >
        <motion.div
          className="relative"
          animate={
            shouldReduceMotion
              ? {}
              : {
                  scale: [1, 1.05, 1],
                  y: [0, -10, 0],
                }
          }
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {/* Large central bubble */}
          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 shadow-2xl shadow-softBlue/30 dark:shadow-brightPurple/30 border-4 border-softBlue/40 dark:border-brightPurple/40 flex items-center justify-center p-4 hover:border-softBlue/60 dark:hover:border-brightPurple/60 hover:shadow-softBlue/40 dark:hover:shadow-brightPurple/40 transition-all duration-300">
            <img
              src={TrackNListLogo}
              alt="TrackNList"
              className="w-full h-full object-contain"
            />
            {/* Bubble shine effect */}
            <div className="absolute top-3 left-3 w-8 h-8 rounded-full bg-white/50 dark:bg-white/20 blur-md" />
            <div className="absolute top-4 left-4 w-4 h-4 rounded-full bg-white/60 dark:bg-white/30 blur-sm" />
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

const SignupForm: React.FC = () => {
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
    plan: "Free",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [message, setMessage] = useState<string | null>(null);
  const [messageSuccess, setMessageSuccess] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.getElementById("header")!.classList.add("bg-white");
    return () => {
      document.getElementById("header")!.classList.remove("bg-white");
    };
  }, []);

  useEffect(() => {
    storeAcquisitionContext();
    track(
      "AUTH:SIGNUP_VIEWED",
      {
        page_kind: "signup",
      },
      {
        routePath: "/signup",
        eventOwner: "frontend_intent",
        nonInteraction: true,
      },
    );
    const params = new URLSearchParams(location.search);
    const plan = params.get("plan");
    if (plan && plans.includes(plan)) {
      setFormData((prevData) => ({ ...prevData, plan }));
      track("AUTH:SIGNUP_PLAN_PREFILLED", {
        selected_plan: plan,
        billing_interval: params.get("interval") || "month",
      });
    }
  }, [location.search]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }
    return newErrors;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handlePlanChange = (plan: string) => {
    setFormData({
      ...formData,
      plan,
    });
    track("AUTH:SIGNUP_PLAN_SELECTED", {
      selected_plan: plan,
      billing_interval:
        new URLSearchParams(location.search).get("interval") || "month",
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      track("AUTH:SIGNUP_SUBMIT_VALIDATION_FAILED", {
        failure_code: Object.keys(validationErrors).join(","),
        selected_plan: formData.plan,
        billing_interval:
          new URLSearchParams(location.search).get("interval") || "month",
      });
      return;
    }
    setIsSubmitting(true);
    const params = new URLSearchParams(location.search);
    const interval = params.get("interval") || "month";
    const acquisition = getAcquisitionContext();
    track("AUTH:SIGNUP_STARTED", {
      selected_plan: formData.plan,
      billing_interval: interval,
      utm_source: acquisition.utmSource,
      utm_medium: acquisition.utmMedium,
      utm_campaign: acquisition.utmCampaign,
    });
    try {
      const response = await fetch("https://api.tracknlist.com/api/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAnalyticsHeaders("/signup"),
        },
        body: JSON.stringify({
          ...formData,
          plan: getPriceId(formData.plan, interval),
        }),
      });

      if (response.ok) {
        setMessageSuccess(true);
        setMessage("Verification email sent. Please check your inbox.");
      } else {
        const errorData = await response.json();
        track("AUTH:SIGNUP_SUBMIT_API_FAILED", {
          failure_code: errorData.error || "signup_api_failed",
          selected_plan: formData.plan,
          billing_interval: interval,
        });
        setMessage(errorData.error || "An error occurred. Please try again.");
      }
    } catch (error) {
      track("AUTH:SIGNUP_SUBMIT_API_FAILED", {
        failure_code: "signup_network_failed",
        selected_plan: formData.plan,
        billing_interval: interval,
      });
      setMessage("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const redirectToWhopOAuth = async () => {
    track("AUTH:WHOP_OAUTH_STARTED", {
      selected_plan: formData.plan,
      source_surface: "site",
    });
    const clientId = "1_c8LXzyL1KNRvgTYfs6BdH7KS4GPCA1fH4dfIFKjh8";
    const redirectUri = encodeURIComponent(
      "https://api.tracknlist.com/api/whop/callback",
    );
    window.location.href = `https://whop.com/oauth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code`;
  };

  return (
    <>
      <div className="flex min-h-full flex-1 dark:bg-gray-800 sm:dark:bg-gray-900 mt-[40px]">
        <div className="flex flex-1 flex-col justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <h2 className="mt-8 text-center sm:text-left text-2xl font-bold leading-9 tracking-tight text-gray-900 dark:text-white">
                Create your account
              </h2>
              <p className="mt-2 text-center sm:text-left text-sm leading-6 text-gray-500 dark:text-gray-400">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-semibold text-indigo-600 hover:text-indigo-500"
                >
                  Sign in
                </Link>
              </p>
            </div>

            <div className="mt-5 sm:mt-10">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <Input
                  id="email"
                  name="email"
                  type="email"
                  label="Email address"
                  value={formData.email}
                  onChange={handleChange}
                  error={errors.email}
                  required
                  autoComplete="email"
                />

                <Input
                  id="username"
                  name="username"
                  type="text"
                  label="Username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  autoComplete="username"
                />

                <Input
                  id="firstName"
                  name="firstName"
                  type="text"
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  autoComplete="given-name"
                />

                <Input
                  id="lastName"
                  name="lastName"
                  type="text"
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  autoComplete="family-name"
                />

                <Input
                  id="password"
                  name="password"
                  type="password"
                  label="Password"
                  value={formData.password}
                  onChange={handleChange}
                  error={errors.password}
                  required
                  autoComplete="new-password"
                />

                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={errors.confirmPassword}
                  required
                  autoComplete="new-password"
                />

                <div>
                  <label
                    htmlFor="plan"
                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Select Plan
                  </label>
                  <div className="mt-2 w-full">
                    <Menu
                      as="div"
                      className="relative inline-block text-left w-full"
                    >
                      <div>
                        <MenuButton className="flex items-center justify-between w-full rounded-md border-0 py-1.5 px-3.5 text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 dark:bg-gray-700 dark:focus:ring-indigo-500 sm:text-sm sm:leading-6">
                          {formData.plan}
                          <ChevronDownIcon
                            aria-hidden="true"
                            className="-mr-1 h-5 w-5 text-gray-400"
                          />
                        </MenuButton>
                      </div>

                      <MenuItems
                        transition
                        className="absolute left-0 z-10 mt-2 w-full origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in dark:bg-gray-800 dark:ring-gray-700"
                      >
                        <div className="py-1">
                          {plans.map((plan) => (
                            <MenuItem key={plan}>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => handlePlanChange(plan)}
                                  className={`block w-full px-4 py-2 text-left text-sm ${
                                    active
                                      ? "bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-white"
                                      : "text-gray-700 dark:text-white"
                                  }`}
                                >
                                  {plan}
                                </button>
                              )}
                            </MenuItem>
                          ))}
                        </div>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>

                <div className="space-y-3">
                  {SIGNUP_DISABLED ? (
                    <>
                      <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-3">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                              We're experiencing some technical difficulties. We'll be back soon.
                            </h3>
                          </div>
                        </div>
                      </div>
                      <button
                        type="submit"
                        disabled={true}
                        className="flex w-full justify-center rounded-md bg-gray-300 dark:bg-gray-700 px-3 py-1.5 text-sm font-semibold leading-6 text-gray-500 dark:text-gray-400 shadow-sm cursor-not-allowed"
                      >
                        Sign up
                      </button>
                    </>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex w-full justify-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                      {isSubmitting ? (
                        <svg
                          className="animate-spin h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        "Sign up"
                      )}
                    </button>
                  )}
                </div>
              </form>
              {message && (
                <div
                  className={`my-2 text-sm ${
                    messageSuccess
                      ? "text-green-500 dark:text-green-400"
                      : "text-red-600 dark:text-red-400"
                  }`}
                >
                  {message}
                </div>
              )}
              <div className="mt-10">
                <div className="relative">
                  <div
                    aria-hidden="true"
                    className="absolute inset-0 flex items-center"
                  >
                    <div className="w-full border-t border-gray-200 dark:border-gray-700" />
                  </div>
                  <div className="relative flex justify-center text-sm font-medium leading-6">
                    <span className="bg-white dark:bg-gray-900 px-6 text-gray-900 dark:text-white">
                      Or continue with
                    </span>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4">
                  <a
                    href="https://api.tracknlist.com/api/google/login"
                    className="flex w-full items-center justify-center gap-3 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:ring-transparent dark:bg-gray-800 dark:text-white dark:ring-gray-700"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-5 w-5"
                    >
                      <path
                        d="M12.0003 4.75C13.7703 4.75 15.3553 5.36002 16.6053 6.54998L20.0303 3.125C17.9502 1.19 15.2353 0 12.0003 0C7.31028 0 3.25527 2.69 1.28027 6.60998L5.27028 9.70498C6.21525 6.86002 8.87028 4.75 12.0003 4.75Z"
                        fill="#EA4335"
                      />
                      <path
                        d="M23.49 12.275C23.49 11.49 23.415 10.73 23.3 10H12V14.51H18.47C18.18 15.99 17.34 17.25 16.08 18.1L19.945 21.1C22.2 19.01 23.49 15.92 23.49 12.275Z"
                        fill="#4285F4"
                      />
                      <path
                        d="M5.26498 14.2949C5.02498 13.5699 4.88501 12.7999 4.88501 11.9999C4.88501 11.1999 5.01998 10.4299 5.26498 9.7049L1.275 6.60986C0.46 8.22986 0 10.0599 0 11.9999C0 13.9399 0.46 15.7699 1.28 17.3899L5.26498 14.2949Z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12.0004 24.0001C15.2404 24.0001 17.9654 22.935 19.9454 21.095L16.0804 18.095C15.0054 18.82 13.6204 19.245 12.0004 19.245C8.8704 19.245 6.21537 17.135 5.2654 14.29L1.27539 17.385C3.25539 21.31 7.3104 24.0001 12.0004 24.0001Z"
                        fill="#34A853"
                      />
                    </svg>
                    <span className="text-sm font-semibold leading-6">
                      Google
                    </span>
                  </a>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4">
                  <button
                    type="button"
                    onClick={redirectToWhopOAuth}
                    className="flex w-full items-center cursor-pointer justify-center gap-3 rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 focus-visible:ring-transparent"
                  >
                    <img
                      src={WhopColored}
                      alt={"whop logo"}
                      className={"h-5 w-7"}
                    />
                    <span className="text-sm font-semibold leading-6">
                      Whop
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative hidden w-0 flex-1 lg:block overflow-hidden bg-gradient-to-br from-softBlue/10 via-brightPurple/10 to-deepPurple/10 dark:from-softBlue/5 dark:via-brightPurple/5 dark:to-deepPurple/5">
          <SignupAnimation />
        </div>
      </div>
    </>
  );
};

export default SignupForm;
